import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, XCircle, ShieldCheck, FileText, PoundSterling, ClipboardList,
  Users, HeartHandshake, ArrowRight, AlertTriangle, Sparkles, NotebookPen,
  Scale, Stethoscope, Banknote, Info, Play
} from "lucide-react";
import { DisclaimerBar, SectionHeading } from "../components/shared/Primitives";
import FeaturedArticleStrip from "../components/shared/FeaturedArticleStrip";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/0162b9c5-f9a9-48a7-94a6-099ad732f404/images/c0a51cf82a6004eae4ec0a84e68f81cc1f4f27ae89363d6f4849ea01f0015a10.png";

const EXPLAINER_VIDEO_URL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_624585f0-b81c-459e-9a59-b27e85d0ead7/artifacts/922d88feea34f3c2__CDR%20Storyboard%202A.mp4";
const EXPLAINER_POSTER = "/jcc-explainer-poster.jpg";

// Click-to-play only (no autoplay): the <video> element itself isn't
// mounted (so nothing is fetched) until the visitor clicks the play
// button — keeps the homepage load light.
function ExplainerVideoSection() {
  const [playing, setPlaying] = useState(false);
  return (
    <section className="section" data-testid="jcc-explainer-video-section">
      <div className="container-jcc">
        <SectionHeading
          eyebrow="See it in action"
          title="See How Just Care Community Works"
          description="From finding the right match to agreeing the details — see how JCC helps families and independent carers move forward with clarity and confidence."
        />
        <div className="mt-10 max-w-4xl mx-auto">
          <div
            className="relative w-full aspect-video rounded-3xl overflow-hidden bg-ink shadow-xl"
            data-testid="jcc-explainer-video-wrapper"
          >
            {playing ? (
              <video
                className="w-full h-full"
                controls
                autoPlay
                playsInline
                poster={EXPLAINER_POSTER}
                data-testid="jcc-explainer-video-player"
              >
                <source src={EXPLAINER_VIDEO_URL} type="video/mp4" />
                Your browser doesn't support embedded video.
              </video>
            ) : (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                className="group absolute inset-0 w-full h-full cursor-pointer"
                aria-label="Play: See How Just Care Community Works"
                data-testid="jcc-explainer-play-button"
              >
                <img
                  src={EXPLAINER_POSTER}
                  alt="Preview of the Just Care Community explainer video"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <span className="absolute inset-0 bg-black/25 group-hover:bg-black/35 transition-colors duration-300" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-7 h-7 sm:w-8 sm:h-8 text-teal ml-1" fill="currentColor" strokeWidth={0} />
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/85 to-white" />
        </div>
        <div className="container-jcc pt-20 md:pt-28 pb-16 md:pb-24">
          <div className="max-w-4xl">
            <span className="pill bg-teal-50 text-teal-700 mb-6" data-testid="hero-pill">UK Independent Care Community</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight">
              Independent care,<br />
              <span className="text-teal">done properly.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-ink-muted leading-relaxed max-w-2xl">
              Just Care Community helps families and independent carers create fairer care arrangements with the structure, clarity and practical safeguards that good care depends on.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <Link
                to="/for-carers"
                className="group flex flex-col rounded-2xl border-2 border-purple/20 bg-white p-6 hover:border-purple hover:-translate-y-1 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-2"
                data-testid="hero-cta-carer"
              >
                <span className="overline text-purple mb-2">For independent carers</span>
                <h3 className="font-display font-semibold text-xl leading-snug">
                  Build your care business with greater structure
                </h3>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                  Set clearer rates, strengthen agreements, organise records and finances, and establish safer professional boundaries.
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-purple font-semibold text-sm">
                  Explore support for carers
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
              <Link
                to="/for-families"
                className="group flex flex-col rounded-2xl border-2 border-teal/20 bg-white p-6 hover:border-teal hover:-translate-y-1 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2"
                data-testid="hero-cta-family"
              >
                <span className="overline text-teal mb-2">For families</span>
                <h3 className="font-display font-semibold text-xl leading-snug">
                  Plan private care with greater confidence
                </h3>
                <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                  Understand what support is needed, approach independent carers, agree expectations and keep the practical arrangement organised.
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-teal font-semibold text-sm">
                  Explore support for families
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </div>
            <div className="mt-4">
              <Link to="/resources" className="btn-outline text-sm" data-testid="hero-cta-resources">Browse free resources</Link>
            </div>
            <div className="mt-10 max-w-2xl">
              <DisclaimerBar
                icon={Info}
                variant="info"
                title="JCC is not a care agency."
                description="We do not employ, supply, select, vet, manage or supervise carers. Families and independent carers make their own decisions and agree arrangements directly. JCC provides practical tools, guidance and structure to help them approach those decisions more clearly and confidently."
              />
            </div>
          </div>
        </div>
      </section>

      {/* EXPLAINER VIDEO */}
      <ExplainerVideoSection />

      {/* WHAT JCC IS / IS NOT */}
      <section className="section bg-surface">
        <div className="container-jcc">
          <SectionHeading
            eyebrow="Honest positioning"
            title="What JCC is — and what it isn't."
            description="Clarity from the start is a value, not a disclaimer. Because independent care works best when everyone understands where they stand."
          />
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className="card-base p-8" data-testid="what-we-are">
              <div className="flex items-center gap-2 text-teal font-semibold mb-4"><CheckCircle2 className="w-5 h-5" /> JCC is</div>
              <ul className="space-y-3 text-ink-muted">
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-teal shrink-0 mt-0.5" /><span>Practical tools, guidance and structured support designed for real-world independent care.</span></li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-teal shrink-0 mt-0.5" /><span>Plain-English help with rates, agreements, boundaries, payments and professional records.</span></li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-teal shrink-0 mt-0.5" /><span>A more private and organised way for families and independent carers to find and approach one another.</span></li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-teal shrink-0 mt-0.5" /><span>A growing community working towards fairer, clearer and better-structured independent care.</span></li>
              </ul>
            </div>
            <div className="card-base p-8" data-testid="what-we-are-not">
              <div className="flex items-center gap-2 text-purple font-semibold mb-4"><XCircle className="w-5 h-5" /> JCC is not</div>
              <ul className="space-y-3 text-ink-muted">
                <li className="flex gap-3"><XCircle className="w-5 h-5 text-purple shrink-0 mt-0.5" /><span>A care agency, employer or care provider.</span></li>
                <li className="flex gap-3"><XCircle className="w-5 h-5 text-purple shrink-0 mt-0.5" /><span>CQC registered, because JCC does not provide regulated care.</span></li>
                <li className="flex gap-3"><XCircle className="w-5 h-5 text-purple shrink-0 mt-0.5" /><span>Responsible for selecting, vetting, managing or supervising carers or care arrangements.</span></li>
                <li className="flex gap-3"><XCircle className="w-5 h-5 text-purple shrink-0 mt-0.5" /><span>A guarantee of suitability, availability, placements or outcomes.</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* THE JCC ECOSYSTEM — visual overview of the care-partnership journey */}
      <section className="section" data-testid="jcc-ecosystem">
        <div className="container-jcc">
          <SectionHeading
            eyebrow="How it works"
            title="The JCC Ecosystem"
            description="A simple visual guide to how independent carers and families can move from first contact to a safer, clearer, more professional care partnership."
          />
          <div className="mt-12 flex justify-center">
            <img
              src="/images/jcc-ecosystem.png"
              alt="The JCC Ecosystem care partnership model showing the journey for clients and carers from profiles and care needs through to a shared Care Partnership Room."
              loading="lazy"
              className="w-full max-w-[1100px] h-auto rounded-2xl"
              data-testid="jcc-ecosystem-image"
            />
          </div>
        </div>
      </section>

      {/* EDITOR'S PICK — pinned WIM article */}
      <FeaturedArticleStrip />

      {/* FOR CARERS */}
      <section className="section">
        <div className="container-jcc">
          <SectionHeading eyebrow="For carers" accent="purple"
            title="Work independently. Get paid fairly. Stay protected."
            description="Everything you need to work as a private, independent carer — with more clarity, confidence, protection and control over your earnings." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: "Start working independently", body: "Move from agency/company rotations to independent work with clearer boundaries, better structure and proper support." },
              { icon: ShieldCheck, title: "Protect yourself with written agreements", body: "Booking agreements, notice terms, cancellation and deposit clauses that are fair to both sides." },
              { icon: NotebookPen, title: "Use professional shift records", body: "Arrival checks, daily care logs, medication records and handover notes — the same standard as regulated care." },
              { icon: PoundSterling, title: "Understand pricing, tax and boundaries", body: "Work out a rate that covers real costs. Learn what's an allowable expense. Say no without losing clients." },
              { icon: AlertTriangle, title: "Spot red flags early", body: "Know the signals of unsafe arrangements, scope creep and slow payers — before they become a problem." },
              { icon: HeartHandshake, title: "Access Pro Carer tools", body: "Templates, calculators, training guidance and payment protection resources — all in one place." },
            ].map((c) => (
              <div key={c.title} className="card-base p-6 hover:-translate-y-1 transition-transform duration-300" data-testid={`carer-card-${c.title.toLowerCase().split(' ').slice(0,2).join('-')}`}>
                <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple flex items-center justify-center mb-4">
                  <c.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display font-semibold text-xl leading-snug">{c.title}</h3>
                <p className="mt-2 text-ink-muted text-sm leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex gap-3">
            <Link to="/pro-carer-circle" className="btn-purple" data-testid="home-cta-pro">Explore Pro Carer Office</Link>
            <Link to="/for-carers" className="btn-outline">Learn more</Link>
          </div>
        </div>
      </section>

      {/* FOR FAMILIES */}
      <section className="section bg-surface-family">
        <div className="container-jcc">
          <SectionHeading eyebrow="For families" accent="teal"
            title="Arrange safer care without agency fees."
            description="Independent care can work beautifully — but only when expectations, responsibilities and boundaries are clear from the start." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Users, title: "Understand independent care", body: "Understand how independent care works, who is responsible for what, and when a traditional care provider or agency may be the safer option." },
              { icon: ClipboardList, title: "Know what to ask before hiring", body: "Experience, DBS, insurance, references — the interview checklist most families forget." },
              { icon: FileText, title: "Use clear written agreements", body: "Care booking agreement, payment terms, schedules and expectations — all in plain English." },
              { icon: AlertTriangle, title: "Avoid unsafe informal arrangements", body: "Cash-in-hand with no records is risky for everyone. We'll show you why — and what to do instead." },
              { icon: Banknote, title: "Reduce agency costs fairly", body: "Pay a carer fairly while often spending less than you would through an agency or care company. Everyone benefits from clearer, more direct arrangements." },
              { icon: Scale, title: "Protect your loved one", body: "Safeguarding pathways, escalation routes and the paperwork that protects them if something goes wrong." },
            ].map((c) => (
              <div key={c.title} className="card-base p-6 hover:-translate-y-1 transition-transform duration-300" data-testid={`family-card-${c.title.toLowerCase().split(' ').slice(0,2).join('-')}`}>
                <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal flex items-center justify-center mb-4">
                  <c.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display font-semibold text-xl leading-snug">{c.title}</h3>
                <p className="mt-2 text-ink-muted text-sm leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex gap-3">
            <a
              href="/join/family-starter"
              className="btn-teal"
              data-testid="home-cta-family-access"
            >
              Join Family Starter — free
            </a>
            <Link to="/for-families" className="btn-outline">Learn more</Link>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP */}
      <section className="section">
        <div className="container-jcc">
          <SectionHeading eyebrow="Membership" title="Clear, practical support — without the agency middleman."
            description="Pick what fits. No auto-renew traps. No hidden upsells." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="rounded-3xl border border-slate-200 p-8 bg-white" data-testid="plan-free">
              <div className="overline text-ink-soft">Carer Starter</div>
              <div className="mt-3 flex items-end gap-1"><span className="text-4xl font-bold">£0</span><span className="text-ink-muted mb-1">forever</span></div>
              <p className="mt-3 text-sm text-ink-muted">Starter templates, community articles and the fundamentals of independent care.</p>
              <a href="/join/carer-starter" className="btn-outline mt-6 w-full">Join free</a>
            </div>
            <div className="rounded-3xl border-2 border-purple p-8 bg-surface-carer relative" data-testid="plan-pro">
              <span className="pill bg-purple text-white absolute -top-3 left-8">Most popular</span>
              <div className="overline text-purple">Pro Carer Office</div>
              <div className="mt-3 flex items-end gap-1"><span className="text-4xl font-bold">£19</span><span className="text-ink-muted mb-1">/month</span></div>
              <p className="mt-3 text-sm text-ink-muted">Full toolkit, templates, calculators, CPD guidance, tax help. 7-day free trial.</p>
              <a href="/membership/pro-carer-circle" className="btn-purple mt-6 w-full">Start 7-day free trial</a>
            </div>
            <div className="rounded-3xl border border-slate-200 p-8 bg-white" data-testid="plan-family-free">
              <div className="overline text-ink-soft">Family Starter</div>
              <div className="mt-3 flex items-end gap-1"><span className="text-4xl font-bold">£0</span><span className="text-ink-muted mb-1">forever</span></div>
              <p className="mt-3 text-sm text-ink-muted">Introductory guides, sample interview questions and the basics of arranging independent care.</p>
              <a href="/join/family-starter" className="btn-outline mt-6 w-full">Join free</a>
            </div>
            <div className="rounded-3xl border border-slate-200 p-8 bg-surface-family" data-testid="plan-family">
              <div className="overline text-teal">Family Care Office</div>
              <div className="mt-3 flex items-end gap-1"><span className="text-4xl font-bold">£19</span><span className="text-ink-muted mb-1">/month</span></div>
              <p className="mt-3 text-sm text-ink-muted">Everything a family needs to arrange private care safely — agreements, checklists, pricing guidance.</p>
              <Link to="/family-access" className="btn-teal mt-6 w-full inline-block text-center">Explore Family Care Office</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section">
        <div className="container-jcc">
          <div className="rounded-3xl bg-gradient-to-br from-teal to-purple text-white p-10 md:p-16 relative overflow-hidden">
            <div className="relative z-10 max-w-3xl">
              <Stethoscope className="w-10 h-10 opacity-80" strokeWidth={1.5} />
              <h2 className="mt-6 text-3xl md:text-5xl font-bold leading-tight">Build safer independent care arrangements — with clarity from the start.</h2>
              <p className="mt-4 text-white/85 text-lg max-w-2xl">Join thousands of carers and families who are moving on from opaque agency models. Structure, not stress.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="/join/carer-starter" className="bg-white text-ink hover:bg-slate-100 rounded-full px-6 py-3 font-medium" data-testid="final-cta-join">Join free</a>
                <Link to="/resources" className="border border-white/40 hover:bg-white/10 rounded-full px-6 py-3 font-medium">Explore Resources</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
